import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { VolumeOff, KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumb from '../../shared/Breadcrumb';
import Navbar from '../../shared/Navbar';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import TeamNotifications from './TeamNotifications';
import { NOTIFICATION_SETTINGS, USER_ROLES } from '../../../shared/appConstants';
import { hasStoredNotificationSoundPreference, isNotificationSoundSilent, setNotificationSoundSilent } from '../../../shared/audioUtils';
import { api, ENDPOINTS } from '../../../services';
import { fetchProjectDropdownsByUser } from '../../../store/ticketSlice';
import './NotificationSettings.scss';

const PERSIST_DEBOUNCE_MS = 650;
const MIN_ENABLED_NOTIFICATION_CONFIGS = 3;
const PROJECT_SELECTION_PREFERENCE_KEY = 'notification_setting';
const PROJECT_SELECTION_FIELDS = {
  MY: 'my-notification',
  TEAM: 'team-notification'
};
const PROJECT_SELECTION_REQUEST_CACHE_KEY = 'notification_setting';
const projectPreferenceRequestCache = new Map();
const arePreferencesEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const DEFAULT_DISABLED_NOTIFICATION_ROWS = new Set([
  'comment_on_task',
  'reply_to_comment',
  'task_status_change',
  'worklog_update'
]);
const getInitialSilentNotificationSound = (projectId = null) => (
  hasStoredNotificationSoundPreference(projectId) ? isNotificationSoundSilent(projectId) : false
);
const getPreferenceApiErrorMessage = (error, fallbackMessage) => {
  const apiError =
    error?.response?.data?.error
    || error?.response?.data?.message
    || error?.response?.data?.data?.error
    || error?.response?.data?.data?.message
    || error?.response?.data?.errors?.[0]?.message
    || error?.response?.data?.errors?.[0]?.error;

  return apiError || error?.message || fallbackMessage;
};

const extractPreferencePayload = (data) => (
  data?.notification_config
  || data?.notificationConfig
  || data?.preference_value
  || data?.preferenceValue
  || data?.data?.notification_config
  || data?.data?.notificationConfig
  || data?.data?.preference_value
  || data?.data?.preferenceValue
  || data
);

const normalizeProjectSelectionPreferences = (data) => {
  const payload = extractPreferencePayload(data);

  if (!payload || typeof payload !== 'object') {
    return {
      [PROJECT_SELECTION_FIELDS.MY]: null,
      [PROJECT_SELECTION_FIELDS.TEAM]: null
    };
  }

  const selectionMap = payload.notification_setting
    || payload.notificationSetting
    || payload.preference_value?.notification_setting
    || payload.preferenceValue?.notification_setting
    || payload.preference_value?.notificationSetting
    || payload.preferenceValue?.notificationSetting
    || payload.notification_config?.notification_setting
    || payload.notificationConfig?.notification_setting
    || payload.notification_config?.notificationSetting
    || payload.notificationConfig?.notificationSetting
    || payload;

  return {
    [PROJECT_SELECTION_FIELDS.MY]:
      selectionMap[PROJECT_SELECTION_FIELDS.MY]
      ?? selectionMap.my_notification
      ?? selectionMap.myNotification
      ?? null,
    [PROJECT_SELECTION_FIELDS.TEAM]:
      selectionMap[PROJECT_SELECTION_FIELDS.TEAM]
      ?? selectionMap.team_notification
      ?? selectionMap.teamNotification
      ?? null
  };
};

const hasNotificationPreferenceConfig = (data) => {
  const payload = extractPreferencePayload(data);
  return Boolean(
    payload
    && typeof payload === 'object'
    && (
      payload.notification_setting
      || payload.notificationSetting
      || payload.default_notification
      || payload.defaultNotification
      || payload.muted_notification_for_next
      || payload.mutedNotificationForNext
      || payload.notification_enabled !== undefined
      || payload.notificationEnabled !== undefined
    )
  );
};

const fetchCachedPreference = (cacheKey, requestFn) => {
  if (projectPreferenceRequestCache.has(cacheKey)) {
    return projectPreferenceRequestCache.get(cacheKey);
  }

  const requestPromise = requestFn().finally(() => {
    projectPreferenceRequestCache.delete(cacheKey);
  });

  projectPreferenceRequestCache.set(cacheKey, requestPromise);
  return requestPromise;
};

const findProjectById = (projects = [], projectId = null) => (
  (Array.isArray(projects) ? projects : []).find(project => String(project.id) === String(projectId)) || null
);

const areSelectedProjectsEqual = (left = {}, right = {}) => (
  String(left?.[PROJECT_SELECTION_FIELDS.MY] || '') === String(right?.[PROJECT_SELECTION_FIELDS.MY] || '')
  && String(left?.[PROJECT_SELECTION_FIELDS.TEAM] || '') === String(right?.[PROJECT_SELECTION_FIELDS.TEAM] || '')
);

const NotificationSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector(state => state.auth);
  const { projectDropdownsByUser, projectDropdownsByUserLoading } = useSelector(state => state.tickets);
  const selectedProjectFromTickets = useSelector((state) => state.tickets?.filters?.selectedProject);
  const prevTicketProjectRef = React.useRef(null);
  const loadFailedMessage = t('notificationSettings.errors.loadFailed');
  const saveFailedMessage = t('notificationSettings.errors.saveFailed');
  const [preferences, setPreferences] = useState(() => NOTIFICATION_SETTINGS.createDefaultPreferences());
  const [muteSelection, setMuteSelection] = useState('none');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [projectMenuAnchor, setProjectMenuAnchor] = useState(null);
  const [, setProjectMenuTab] = useState(null); // Track which tab opened the menu
  const [mySelectedProjectId, setMySelectedProjectId] = useState(null);
  const [teamSelectedProjectId, setTeamSelectedProjectId] = useState(null);
  const [isProjectSelectionPreferenceLoaded, setIsProjectSelectionPreferenceLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('my'); // 'my' or 'team'
  const preferencesRef = useRef(preferences);
  const lastPersistedPreferencesRef = useRef(preferences);
  const saveTimeoutRef = useRef(null);
  const projectSelectionSaveTimeoutRef = useRef(null);
  const lastPersistedProjectSelectionsRef = useRef({
    [PROJECT_SELECTION_FIELDS.MY]: null,
    [PROJECT_SELECTION_FIELDS.TEAM]: null
  });
  const seededInitialProjectSelectionRef = useRef(false);
  const projectSelectionPreferenceFetchedRef = useRef(false);
  const hasLoadedProjectSelectionsRef = useRef(false);
  const hasAvailableProjects = Array.isArray(projectDropdownsByUser) && projectDropdownsByUser.length > 0;
  const mySelectedProject = useMemo(
    () => findProjectById(projectDropdownsByUser, mySelectedProjectId),
    [mySelectedProjectId, projectDropdownsByUser]
  );
  const teamSelectedProject = useMemo(
    () => findProjectById(projectDropdownsByUser, teamSelectedProjectId),
    [teamSelectedProjectId, projectDropdownsByUser]
  );
  const channelColumns = useMemo(() => {
    return [
      {
        id: NOTIFICATION_SETTINGS.CHANNELS.EMAIL,
        labelKey: 'notificationSettings.table.headerEmail'
      },
      {
        id: NOTIFICATION_SETTINGS.CHANNELS.IN_APP,
        labelKey: 'notificationSettings.table.headerInApp'
      },
      {
        id: 'slack',
        labelKey: 'notificationSettings.table.headerSlack',
        isSlack: true
      }
    ];
  }, []);
  const tableColumnWidths = useMemo(() => {
    if (channelColumns.length === 2) {
      return ['44%', '28%', '28%'];
    }

    if (channelColumns.length === 3) {
      return ['40%', '20%', '20%', '20%'];
    }

    const firstColumnWidth = 42;
    const remainingWidth = 100 - firstColumnWidth;
    const channelColumnWidth = remainingWidth / Math.max(channelColumns.length, 1);

    return [`${firstColumnWidth}%`, ...channelColumns.map(() => `${channelColumnWidth}%`)];
  }, [channelColumns]);
  const isMyNotificationsLoading = activeTab === 'my' && (
    projectDropdownsByUserLoading
    || isInitialLoading
    || (hasAvailableProjects && !mySelectedProjectId)
  );

  const canAccessTeamNotifications = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;
  const hasSlackAuthResult = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return Boolean(searchParams.get('slack_status'));
  }, [location.search]);

  const deriveMuteSelection = useCallback((mutedUntil) => {
    if (!mutedUntil) return 'none';
    const target = new Date(mutedUntil);
    if (Number.isNaN(target.getTime())) return 'none';
    const now = new Date();
    
    // If the mute time has already passed, return 'none'
    if (target.getTime() <= now.getTime()) return 'none';
    
    const diffMinutes = Math.round((target.getTime() - now.getTime()) / 60000);
    if (Math.abs(diffMinutes - 30) <= 5) return '30m';
    if (Math.abs(diffMinutes - 60) <= 5) return '1h';
    if (Math.abs(diffMinutes - 240) <= 5) return '4h';

    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = target.toDateString() === tomorrow.toDateString()
      && target.getHours() === 9
      && target.getMinutes() === 0;
    if (isTomorrow) return 'tomorrow';

    const nextWeek = new Date(now);
    nextWeek.setDate(now.getDate() + 7);
    const isNextWeek = target.toDateString() === nextWeek.toDateString()
      && target.getHours() === 9
      && target.getMinutes() === 0;
    if (isNextWeek) return 'next_week';

    return 'none';
  }, []);

  const normalizePreferences = useCallback((data) => {
    const defaults = {
      ...NOTIFICATION_SETTINGS.createDefaultPreferences(),
      silent_notification_sound: getInitialSilentNotificationSound(mySelectedProjectId)
    };
    
    // Handle the case where notification_config is null - return defaults (all enabled)
    if (data === null || data === undefined) {
      return defaults;
    }
    
    const payload = data?.preference_value || data?.preferenceValue || data;
    if (!payload || typeof payload !== 'object') {
      return defaults;
    }

    const { PREFERENCE_FIELDS, CHANNELS } = NOTIFICATION_SETTINGS;
    const incomingConfig = payload[PREFERENCE_FIELDS.NOTIFICATION_SETTING] || payload.notification_setting;

    const defaultNotification = Array.isArray(payload?.[PREFERENCE_FIELDS.DEFAULT_NOTIFICATION])
      ? payload[PREFERENCE_FIELDS.DEFAULT_NOTIFICATION]
      : defaults.default_notification;
    const hasExplicitSlackConfiguration = NOTIFICATION_SETTINGS.TABLE_ROWS.some((row) => {
      const incomingRow = incomingConfig?.[row.id];
      return incomingRow && Object.prototype.hasOwnProperty.call(incomingRow, 'slack');
    });
    const isSlackEnabled = defaultNotification.includes('slack') || hasExplicitSlackConfiguration;
    const normalizedDefaultNotification = isSlackEnabled && !defaultNotification.includes('slack')
      ? [...defaultNotification, 'slack']
      : defaultNotification;

    const mergedConfig = NOTIFICATION_SETTINGS.TABLE_ROWS.reduce((acc, row) => {
      const incomingRow = incomingConfig?.[row.id] || {};
      acc[row.id] = {
        [CHANNELS.EMAIL]: typeof incomingRow[CHANNELS.EMAIL] === 'boolean'
          ? incomingRow[CHANNELS.EMAIL]
          : defaults.notification_setting[row.id][CHANNELS.EMAIL],
        [CHANNELS.IN_APP]: typeof incomingRow[CHANNELS.IN_APP] === 'boolean'
          ? incomingRow[CHANNELS.IN_APP]
          : defaults.notification_setting[row.id][CHANNELS.IN_APP],
        slack: isSlackEnabled
          ? (typeof incomingRow.slack === 'boolean'
            ? incomingRow.slack
            : !DEFAULT_DISABLED_NOTIFICATION_ROWS.has(row.id))
          : false
      };
      return acc;
    }, {});

    let mutedUntil = payload[PREFERENCE_FIELDS.MUTED_NOTIFICATION_FOR_NEXT] || defaults.muted_notification_for_next;
    
    // Clear expired mute time on load
    if (mutedUntil) {
      const target = new Date(mutedUntil);
      if (!Number.isNaN(target.getTime()) && target.getTime() <= new Date().getTime()) {
        mutedUntil = null;
      }
    }

    return {
      notification_setting: mergedConfig,
      notification_enabled: true,
      silent_notification_sound: getInitialSilentNotificationSound(mySelectedProjectId),
      default_notification: normalizedDefaultNotification,
      muted_notification_for_next: mutedUntil
    };
  }, [mySelectedProjectId]);

  const buildPreferenceValue = useCallback((nextPreferences) => ({
    [NOTIFICATION_SETTINGS.PREFERENCE_FIELDS.NOTIFICATION_SETTING]: nextPreferences.notification_setting,
    [NOTIFICATION_SETTINGS.PREFERENCE_FIELDS.NOTIFICATION_ENABLED]: true,
    [NOTIFICATION_SETTINGS.PREFERENCE_FIELDS.DEFAULT_NOTIFICATION]: nextPreferences.default_notification,
    [NOTIFICATION_SETTINGS.PREFERENCE_FIELDS.MUTED_NOTIFICATION_FOR_NEXT]: nextPreferences.muted_notification_for_next
  }), []);

  const persistPreferences = useCallback(async (nextPreferences) => {
    if (!mySelectedProjectId) {
      return;
    }

    const body = {
      preference_key: NOTIFICATION_SETTINGS.PREFERENCE_KEY,
      preference_value: buildPreferenceValue(nextPreferences)
    };
    
    try {
      await api.post(ENDPOINTS.SAVE_USER_PREFERENCES, body, {
        params: {
          project_id: mySelectedProjectId,
          preference_type: 'USER'
        }
      });
      lastPersistedPreferencesRef.current = nextPreferences;
    } catch (error) {
      const errorMessage = getPreferenceApiErrorMessage(error, saveFailedMessage);

      if (arePreferencesEqual(preferencesRef.current, nextPreferences)) {
        const rollbackPreferences = lastPersistedPreferencesRef.current;
        preferencesRef.current = rollbackPreferences;
        setNotificationSoundSilent(rollbackPreferences.silent_notification_sound, mySelectedProjectId);
        setPreferences(rollbackPreferences);
      }

      toast.error(errorMessage);
    }
  }, [buildPreferenceValue, mySelectedProjectId, saveFailedMessage]);

  const schedulePersist = useCallback((nextPreferences) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      persistPreferences(nextPreferences);
    }, PERSIST_DEBOUNCE_MS);
  }, [persistPreferences]);

  const updatePreferences = useCallback((updater) => {
    const current = preferencesRef.current;
    const nextPreferences = typeof updater === 'function' ? updater(current) : updater;
    preferencesRef.current = nextPreferences;
    setPreferences(nextPreferences);
    schedulePersist(nextPreferences);
  }, [schedulePersist]);

  const persistProjectSelectionPreferences = useCallback(async (nextSelections) => {
    if (!hasAvailableProjects) {
      return;
    }

    const normalizedSelections = {
      [PROJECT_SELECTION_FIELDS.MY]: nextSelections?.[PROJECT_SELECTION_FIELDS.MY] ?? null,
      [PROJECT_SELECTION_FIELDS.TEAM]: nextSelections?.[PROJECT_SELECTION_FIELDS.TEAM] ?? null
    };

    try {
      await api.post(ENDPOINTS.SAVE_USER_PREFERENCES, {
        preference_key: PROJECT_SELECTION_PREFERENCE_KEY,
        preference_value: normalizedSelections
      });
      lastPersistedProjectSelectionsRef.current = normalizedSelections;
    } catch (error) {
      if (areSelectedProjectsEqual(lastPersistedProjectSelectionsRef.current, normalizedSelections)) {
        return;
      }

      toast.error(getPreferenceApiErrorMessage(error, saveFailedMessage));
    }
  }, [hasAvailableProjects, saveFailedMessage]);

  const persistProjectSelectionChange = useCallback((nextMyProjectId, nextTeamProjectId) => {
    persistProjectSelectionPreferences({
      [PROJECT_SELECTION_FIELDS.MY]: nextMyProjectId ?? null,
      [PROJECT_SELECTION_FIELDS.TEAM]: nextTeamProjectId ?? null
    });
  }, [persistProjectSelectionPreferences]);

  useEffect(() => {
    // Fetch project dropdowns
    dispatch(fetchProjectDropdownsByUser());
  }, [dispatch]);

  // Sync both tabs from the Ticket Management project selection on mount / project change.
  // Uses the same guard pattern as Timesheet so manual in-page changes are not overridden.
  useEffect(() => {
    if (!projectDropdownsByUser || projectDropdownsByUser.length === 0) return;
    const hasProjectContext = selectedProjectFromTickets
      && String(selectedProjectFromTickets).toLowerCase() !== 'all';
    if (!hasProjectContext) return;
    if (prevTicketProjectRef.current === String(selectedProjectFromTickets)) return;
    prevTicketProjectRef.current = String(selectedProjectFromTickets);
    const matchedProject = projectDropdownsByUser.find(
      (p) => String(p.id) === String(selectedProjectFromTickets)
    );
    if (matchedProject) {
      setMySelectedProjectId(matchedProject.id);
      setTeamSelectedProjectId(matchedProject.id);
    }
  }, [projectDropdownsByUser, selectedProjectFromTickets]);

  useEffect(() => {
    let isMounted = true;

    if (projectDropdownsByUserLoading || projectSelectionPreferenceFetchedRef.current) {
      return () => {
        isMounted = false;
      };
    }

    const loadProjectSelections = async () => {
      try {
        const response = await fetchCachedPreference(PROJECT_SELECTION_REQUEST_CACHE_KEY, () => (
          api.get(ENDPOINTS.USER_PREFERENCES(PROJECT_SELECTION_PREFERENCE_KEY))
        ));
        const responseData = response?.data?.data || response?.data;
        const normalizedSelections = normalizeProjectSelectionPreferences(responseData);
        const preferencePayload = extractPreferencePayload(responseData);
        const normalizedPreferencesPayload = hasNotificationPreferenceConfig(preferencePayload)
          ? preferencePayload
          : null;
        const fallbackProject = projectDropdownsByUser[0] || null;
        const storedMyProjectId = sessionStorage.getItem('notification_settings_my_project_id');
        const hasSavedProjectSelection = Boolean(
          normalizedSelections[PROJECT_SELECTION_FIELDS.MY]
          || normalizedSelections[PROJECT_SELECTION_FIELDS.TEAM]
        );
        const nextMyProject = findProjectById(projectDropdownsByUser, normalizedSelections[PROJECT_SELECTION_FIELDS.MY])
          || findProjectById(projectDropdownsByUser, storedMyProjectId)
          || fallbackProject;
        const nextTeamProject = findProjectById(projectDropdownsByUser, normalizedSelections[PROJECT_SELECTION_FIELDS.TEAM]) || nextMyProject || fallbackProject;

        if (!isMounted) {
          return;
        }

        projectSelectionPreferenceFetchedRef.current = true;
        lastPersistedProjectSelectionsRef.current = normalizedSelections;
        if (projectDropdownsByUser.length > 0) {
          setMySelectedProjectId(prev => prev ?? (nextMyProject?.id || null));
          setTeamSelectedProjectId(prev => prev ?? (nextTeamProject?.id || null));
        }
        if (nextMyProject?.id) {
          sessionStorage.setItem('notification_settings_my_project_id', String(nextMyProject.id));
        }

        if (
          !seededInitialProjectSelectionRef.current
          && !hasSavedProjectSelection
          && nextMyProject?.id
          && nextTeamProject?.id
        ) {
          seededInitialProjectSelectionRef.current = true;
          lastPersistedProjectSelectionsRef.current = {
            [PROJECT_SELECTION_FIELDS.MY]: nextMyProject.id,
            [PROJECT_SELECTION_FIELDS.TEAM]: nextTeamProject.id
          };
          await persistProjectSelectionPreferences({
            [PROJECT_SELECTION_FIELDS.MY]: nextMyProject.id,
            [PROJECT_SELECTION_FIELDS.TEAM]: nextTeamProject.id
          });
        }

        if (normalizedPreferencesPayload) {
          const resolvedProjectId = nextMyProject?.id || null;
          const normalizedPreferences = {
            ...normalizePreferences(normalizedPreferencesPayload),
            silent_notification_sound: hasStoredNotificationSoundPreference(resolvedProjectId)
              ? isNotificationSoundSilent(resolvedProjectId)
              : false
          };
          setNotificationSoundSilent(normalizedPreferences.silent_notification_sound, resolvedProjectId);
          preferencesRef.current = normalizedPreferences;
          lastPersistedPreferencesRef.current = normalizedPreferences;
          setPreferences(normalizedPreferences);
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        projectSelectionPreferenceFetchedRef.current = true;
        lastPersistedProjectSelectionsRef.current = {
          [PROJECT_SELECTION_FIELDS.MY]: null,
          [PROJECT_SELECTION_FIELDS.TEAM]: null
        };
      } finally {
        if (isMounted) {
          hasLoadedProjectSelectionsRef.current = true;
          setIsProjectSelectionPreferenceLoaded(true);
        }
      }
    };

    loadProjectSelections();

    return () => {
      isMounted = false;
    };
  }, [hasAvailableProjects, normalizePreferences, persistProjectSelectionPreferences, projectDropdownsByUser, projectDropdownsByUserLoading]);

  useEffect(() => {
    let isMounted = true;

    if (projectDropdownsByUserLoading) {
      return () => {
        isMounted = false;
      };
    }

    if (hasAvailableProjects && !mySelectedProjectId) {
      return () => {
        isMounted = false;
      };
    }

    const loadPreferences = async () => {
      setIsInitialLoading(true);

      if (!mySelectedProjectId) {
        const defaultPreferences = {
          ...NOTIFICATION_SETTINGS.createDefaultPreferences(),
          silent_notification_sound: getInitialSilentNotificationSound(mySelectedProjectId)
        };

        if (isMounted) {
          preferencesRef.current = defaultPreferences;
          lastPersistedPreferencesRef.current = defaultPreferences;
          setPreferences(defaultPreferences);
          setIsInitialLoading(false);
        }

        return;
      }

      try {
        const response = await fetchCachedPreference(
          `user:${mySelectedProjectId}`,
          () => api.get(ENDPOINTS.USER_PREFERENCES(NOTIFICATION_SETTINGS.PREFERENCE_KEY), {
            params: {
              project_id: mySelectedProjectId,
              preference_type: 'USER'
            }
          })
        );
        const stored = response.data?.data?.notification_config || response.data?.data?.[NOTIFICATION_SETTINGS.PREFERENCE_KEY];
        if (isMounted) {
          const normalizedPreferences = normalizePreferences(stored);
          setNotificationSoundSilent(normalizedPreferences.silent_notification_sound, mySelectedProjectId);
          preferencesRef.current = normalizedPreferences;
          lastPersistedPreferencesRef.current = normalizedPreferences;
          setPreferences(normalizedPreferences);
        }
      } catch (error) {
        if (isMounted) {
          const defaultPreferences = {
            ...NOTIFICATION_SETTINGS.createDefaultPreferences(),
            silent_notification_sound: getInitialSilentNotificationSound(mySelectedProjectId)
          };
          const errorMessage = getPreferenceApiErrorMessage(error, loadFailedMessage);
          toast.error(errorMessage);
          setNotificationSoundSilent(defaultPreferences.silent_notification_sound, mySelectedProjectId);
          preferencesRef.current = defaultPreferences;
          lastPersistedPreferencesRef.current = defaultPreferences;
          setPreferences(defaultPreferences);
        }
      } finally {
        if (isMounted) {
          setIsInitialLoading(false);
        }
      }
    };

    loadPreferences();
    return () => {
      isMounted = false;
    };
  }, [hasAvailableProjects, loadFailedMessage, normalizePreferences, projectDropdownsByUserLoading, mySelectedProjectId]);

  useEffect(() => () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    if (projectSelectionSaveTimeoutRef.current) {
      clearTimeout(projectSelectionSaveTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (
      projectDropdownsByUserLoading
      || !projectDropdownsByUser?.length
      || !isProjectSelectionPreferenceLoaded
    ) {
      return;
    }

    const savedSlackProjectId = sessionStorage.getItem('slack_connect_project_id');
    const desiredMyProject = findProjectById(projectDropdownsByUser, lastPersistedProjectSelectionsRef.current[PROJECT_SELECTION_FIELDS.MY]);
    const desiredTeamProject = findProjectById(projectDropdownsByUser, savedSlackProjectId)
      || findProjectById(projectDropdownsByUser, lastPersistedProjectSelectionsRef.current[PROJECT_SELECTION_FIELDS.TEAM]);
    const fallbackProject = projectDropdownsByUser[0] || null;

    setMySelectedProjectId(prev => {
      const prevProject = findProjectById(projectDropdownsByUser, prev);
      if (prevProject) {
        return prev;
      }

      return desiredMyProject?.id || fallbackProject?.id || null;
    });

    setTeamSelectedProjectId(prev => {
      const prevProject = findProjectById(projectDropdownsByUser, prev);
      if (prevProject) {
        return prev;
      }

      const nextTeamProject = desiredTeamProject || fallbackProject;
      if (savedSlackProjectId && nextTeamProject && String(nextTeamProject.id) === String(savedSlackProjectId)) {
        sessionStorage.removeItem('slack_connect_project_id');
      }

      return nextTeamProject?.id || null;
    });
  }, [isProjectSelectionPreferenceLoaded, projectDropdownsByUser, projectDropdownsByUserLoading]);

  const countEnabledNotifications = useCallback((notificationSetting) => {
    return NOTIFICATION_SETTINGS.TABLE_ROWS.reduce((count, row) => {
      const rowConfig = notificationSetting[row.id] || {};
      return count + Object.values(rowConfig).filter(val => val === true).length;
    }, 0);
  }, []);

  const handleDefaultChannelChange = useCallback((event) => {
    const { value } = event.target;
    if (value.length === 0) return;
    updatePreferences(prev => {
      const added = value.filter(ch => !prev.default_notification.includes(ch));
      const removed = prev.default_notification.filter(ch => !value.includes(ch));
      if (added.length === 0 && removed.length === 0) return { ...prev, default_notification: value };
      const updatedSetting = { ...prev.notification_setting };
      NOTIFICATION_SETTINGS.TABLE_ROWS.forEach(row => {
        updatedSetting[row.id] = { ...updatedSetting[row.id] };
        added.forEach(ch => { updatedSetting[row.id][ch] = true; });
        removed.forEach(ch => { updatedSetting[row.id][ch] = false; });
      });

      // Check if we need to enable notifications in the other channel to maintain the minimum
      const enabledCount = countEnabledNotifications(updatedSetting);
      if (enabledCount < MIN_ENABLED_NOTIFICATION_CONFIGS && removed.length > 0) {
        const otherChannel = removed[0] === NOTIFICATION_SETTINGS.CHANNELS.EMAIL 
          ? NOTIFICATION_SETTINGS.CHANNELS.IN_APP 
          : NOTIFICATION_SETTINGS.CHANNELS.EMAIL;
        
        if (value.includes(otherChannel)) {
          let toEnable = MIN_ENABLED_NOTIFICATION_CONFIGS - enabledCount;
          for (const row of NOTIFICATION_SETTINGS.TABLE_ROWS) {
            if (toEnable <= 0) break;
            if (!updatedSetting[row.id][otherChannel]) {
              updatedSetting[row.id][otherChannel] = true;
              toEnable--;
            }
          }
        }
      }

      return { ...prev, default_notification: value, notification_setting: updatedSetting };
    });
  }, [updatePreferences, countEnabledNotifications]);

  const handleSilentNotificationSoundChange = useCallback((value) => {
    
    if (preferencesRef.current?.silent_notification_sound === value) {
      return;
    }

    setNotificationSoundSilent(value, mySelectedProjectId);
    setPreferences(prev => ({
      ...prev,
      silent_notification_sound: value
    }));
    preferencesRef.current = {
      ...preferencesRef.current,
      silent_notification_sound: value
    };
  }, [mySelectedProjectId]);

  const handleMuteDurationChange = useCallback((event) => {
    const nextValue = event.target.value;
    setMuteSelection(nextValue);
    if (nextValue === 'none') {
      updatePreferences(prev => ({
        ...prev,
        muted_notification_for_next: null
      }));
    } else {
      const now = new Date();
      let muteUntil = new Date(now);
      
      switch (nextValue) {
        case '30m':
          muteUntil.setMinutes(now.getMinutes() + 30);
          break;
        case '1h':
          muteUntil.setHours(now.getHours() + 1);
          break;
        case '4h':
          muteUntil.setHours(now.getHours() + 4);
          break;
        case 'tomorrow':
          muteUntil.setDate(now.getDate() + 1);
          muteUntil.setHours(9, 0, 0, 0);
          break;
        case 'next_week':
          muteUntil.setDate(now.getDate() + 7);
          muteUntil.setHours(9, 0, 0, 0);
          break;
        default:
          muteUntil = null;
      }
      
      updatePreferences(prev => ({
        ...prev,
        muted_notification_for_next: muteUntil ? muteUntil.toISOString() : null
      }));
    }
  }, [updatePreferences]);

  useEffect(() => {
    setMuteSelection(deriveMuteSelection(preferences.muted_notification_for_next));
  }, [deriveMuteSelection, preferences.muted_notification_for_next]);

  const handleToggle = useCallback((rowId, channelId) => {
    updatePreferences(prev => {
      const currentValue = prev.notification_setting?.[rowId]?.[channelId];
      
      // If trying to turn off, check if we still meet the minimum enabled count
      if (currentValue) {
        const testSetting = {
          ...prev.notification_setting,
          [rowId]: {
            ...prev.notification_setting[rowId],
            [channelId]: false
          }
        };
        
        const enabledCount = countEnabledNotifications(testSetting);
        if (enabledCount < MIN_ENABLED_NOTIFICATION_CONFIGS) {
          return prev;
        }
      }
      
      return {
        ...prev,
        notification_setting: {
          ...prev.notification_setting,
          [rowId]: {
            ...prev.notification_setting[rowId],
            [channelId]: !currentValue
          }
        }
      };
    });
  }, [updatePreferences, countEnabledNotifications]);

  const renderProjectSelector = (tabName) => {
    const project = tabName === 'my' ? mySelectedProject : teamSelectedProject;
    return (
      <Box className="notification-project-selector" onClick={(e) => {
        setProjectMenuAnchor(e.currentTarget);
        setProjectMenuTab(tabName);
      }}>
        <Typography className="notification-project-label notification-project-bracket">
          (
        </Typography>
        <OverflowTooltip
          title={project ? project.name : t('notificationSettings.allProjects')}
          placement="bottom-start"
          tooltipWidth={300}
          component={Typography}
          className="notification-project-label notification-project-name"
        >
          {project ? project.name : t('notificationSettings.allProjects')}
        </OverflowTooltip>
        <KeyboardArrowDown className="notification-project-icon" />
        <Typography className="notification-project-label notification-project-bracket">)</Typography>
      </Box>
    );
  };

  useEffect(() => {
    if (hasSlackAuthResult && canAccessTeamNotifications) {
      setActiveTab('team');
    }
  }, [canAccessTeamNotifications, hasSlackAuthResult]);

  useEffect(() => {
    if (!canAccessTeamNotifications && activeTab === 'team') {
      setActiveTab('my');
    }
  }, [activeTab, canAccessTeamNotifications]);

  return (
    <div className="dashboard">
      <Navbar />

      <main className="main notification-settings">
        <Breadcrumb
          items={[
            { label: t('notificationSettings.breadcrumb.settings'), path: '/settings' },
            { label: t('notificationSettings.breadcrumb.notificationSettings'), path: null }
          ]}
        />

        <Box className={`notification-settings-header-grid ${canAccessTeamNotifications ? '' : 'notification-settings-header-grid--single'}`}>
          <Box
            className={`notification-settings-header notification-settings-header-section ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            <Box
              className={`notification-tab-item ${activeTab === 'my' ? 'active' : ''}`}
            >
              {t('notificationSettings.myNotifications')}
            </Box>
            {renderProjectSelector('my')}
          </Box>
          {canAccessTeamNotifications && (
            <Box
              className={`notification-settings-header notification-settings-header-section ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              <Box
                className={`notification-tab-item ${activeTab === 'team' ? 'active' : ''}`}
              >
                {t('notificationSettings.teamNotifications')}
              </Box>
            </Box>
          )}
        </Box>

        <Menu
          anchorEl={projectMenuAnchor}
          open={Boolean(projectMenuAnchor)}
          onClose={() => {
            setProjectMenuAnchor(null);
            setProjectMenuTab(null);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            style: {
              maxHeight: 200,
              maxWidth: 300,
              overflow: 'auto',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              marginTop: 4
            }
          }}
        >
          {projectDropdownsByUser?.map((project) => {
            const currentProject = mySelectedProject;
            return (
              <MenuItem
                key={project.id}
                onClick={() => {
                  setMySelectedProjectId(project.id);
                  sessionStorage.setItem('notification_settings_my_project_id', String(project.id));
                  persistProjectSelectionChange(project.id, teamSelectedProjectId);
                  setProjectMenuAnchor(null);
                  setProjectMenuTab(null);
                }}
                sx={{
                  fontSize: '14px',
                  py: 1,
                  px: 2,
                  fontWeight: currentProject?.id === project.id ? 600 : 400,
                  minWidth: 0
                }}
              >
                <OverflowTooltip 
                  title={project.name} 
                  tooltipWidth={350} 
                  className="overflow-tooltip-dropdown" 
                  placement="top-start"
                />
              </MenuItem>
            );
          })}
        </Menu>

        {activeTab === 'my' ? (
          <>
            {isMyNotificationsLoading ? (
              <div className="notification-loading-overlay notification-loading-overlay--content">
                <div className="notification-spinner" />
                <Typography className="notification-loading-text">
                  {t('notificationSettings.loadingSettings')}
                </Typography>
              </div>
            ) : (
              <>
                <div className="notification-my-layout">
                  <div className="notification-row notification-row--my-top">
                    <Card elevation={0} className="notification-card notification-card--enable">
                      <CardContent className="notification-card-content">
                        <div className="notification-card-row notification-card-row--enable">
                          <Typography variant="body1" className="notification-card-title">
                            {t('notificationSettings.enable.label')}
                          </Typography>
                          <div className="notification-tabs">
                            <button
                              type="button"
                              className={`notification-tab ${preferences.silent_notification_sound ? 'active' : ''}`}
                              aria-pressed={preferences.silent_notification_sound}
                              onClick={() => handleSilentNotificationSoundChange(true)}
                            >
                              {t('notificationSettings.common.yes', 'Yes')}
                            </button>
                            <button
                              type="button"
                              className={`notification-tab ${!preferences.silent_notification_sound ? 'active' : ''}`}
                              aria-pressed={!preferences.silent_notification_sound}
                              onClick={() => handleSilentNotificationSoundChange(false)}
                            >
                              {t('notificationSettings.common.no', 'No')}
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card elevation={0} className="notification-card notification-card--defaults">
                      <CardContent className="notification-card-content">
                        <div className="notification-card-row notification-card-row--defaults">
                          <Typography variant="body1" className="notification-card-title">
                            {t('notificationSettings.defaults.label')}
                          </Typography>
                          <FormControl className={`notification-select ${preferences.default_notification?.length > 0 ? 'selected' : ''}`}>
                            <Select
                              multiple
                              value={preferences.default_notification || []}
                              onChange={handleDefaultChannelChange}
                              displayEmpty
                              className="notification-select-input"
                              renderValue={(selected) => {
                                if (selected.length === 0) return '';
                                if (selected.length === 1) {
                                  const allOptions = [...NOTIFICATION_SETTINGS.DEFAULT_CHANNEL_OPTIONS, { value: 'slack', labelKey: 'notificationSettings.slack' }];
                                  const option = allOptions.find(opt => opt.value === selected[0]);
                                  return option ? t(option.labelKey) : selected[0];
                                }
                                return `${selected.length} selected`;
                              }}
                            >
                              {[...NOTIFICATION_SETTINGS.DEFAULT_CHANNEL_OPTIONS, { value: 'slack', labelKey: 'notificationSettings.slack' }].map(option => (
                                <MenuItem key={option.value} value={option.value} className="notification-menu-item">
                                  {t(option.labelKey)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="notification-customize-layout">
                    <section className="notification-panel notification-panel--my">
                      <div className="notification-panel-header">
                        <Typography variant="body1" className="notification-card-title">
                          {t('notificationSettings.customize.title')}
                        </Typography>
                      </div>
                      <div className="notification-panel-body">
                        <TableContainer className="notification-table notification-table--with-slack">
                          <Table>
                            <colgroup>
                              {tableColumnWidths.map((width, index) => (
                                <col key={`notification-column-${index}`} style={{ width }} />
                              ))}
                            </colgroup>
                            <TableHead>
                              <TableRow>
                                <TableCell className="notification-table-header">
                                  {t('notificationSettings.table.headerNotificationSettings')}
                                </TableCell>
                                {channelColumns.map(column => (
                                  <TableCell key={column.id} className="notification-table-header">
                                    {t(column.labelKey)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {NOTIFICATION_SETTINGS.TABLE_ROWS.map(row => (
                                <TableRow key={row.id}>
                                  <TableCell className="notification-table-cell">
                                    {t(row.labelKey)}
                                  </TableCell>
                                  {channelColumns.map(column => {
                                    if (column.isSlack) {
                                      const slackValue = !!preferences.notification_setting[row.id]?.[column.id];
                                      return (
                                        <TableCell key={`${row.id}-${column.id}`} className="notification-table-cell notification-table-cell--slack">
                                          <label className="notification-toggle notification-toggle--edge-aware">
                                            <input
                                              type="checkbox"
                                              checked={slackValue}
                                              onChange={() => handleToggle(row.id, column.id)}
                                              data-testid={`slack-toggle-${row.id}`}
                                              aria-label={t('notificationSettings.table.toggleLabel', {
                                                notification: t(row.labelKey),
                                                channel: t(column.labelKey),
                                                defaultValue: `${t(row.labelKey)} ${t(column.labelKey)}`
                                              })}
                                            />
                                            <span className="notification-toggle-track" />
                                            <span className="notification-toggle-thumb" />
                                          </label>
                                        </TableCell>
                                      );
                                    }

                                    const isDisabled = !preferences.default_notification?.includes(column.id);
                                    const currentValue = !!preferences.notification_setting[row.id]?.[column.id];

                                    const testSetting = {
                                      ...preferences.notification_setting,
                                      [row.id]: {
                                        ...preferences.notification_setting[row.id],
                                        [column.id]: false
                                      }
                                    };
                                    const wouldViolateMinimum = currentValue && countEnabledNotifications(testSetting) < MIN_ENABLED_NOTIFICATION_CONFIGS;
                                    const isToggleDisabled = isDisabled || wouldViolateMinimum;

                                    return (
                                      <TableCell key={`${row.id}-${column.id}`} className="notification-table-cell">
                                        <label className={`notification-toggle${isToggleDisabled ? ' notification-toggle--disabled' : ''}`}>
                                          <input
                                            type="checkbox"
                                            checked={currentValue}
                                            onChange={() => handleToggle(row.id, column.id)}
                                            disabled={isToggleDisabled}
                                            aria-label={t('notificationSettings.table.toggleLabel', {
                                              notification: t(row.labelKey),
                                              channel: t(column.labelKey),
                                              defaultValue: `${t(row.labelKey)} ${t(column.labelKey)}`
                                            })}
                                          />
                                          <span className="notification-toggle-track" />
                                          <span className="notification-toggle-thumb" />
                                          {wouldViolateMinimum && !isDisabled && (
                                            <span className="notification-toggle-tooltip">
                                              {t('notificationSettings.table.minimumNotificationsRequired')}
                                            </span>
                                          )}
                                        </label>
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    </section>
                  </div>

                  <Card elevation={0} className="notification-card notification-card--mute-side">
                    <CardContent className="notification-card-content">
                      <div className="notification-card-row notification-card-row--mute">
                        <Typography variant="body1" className="notification-card-title mute-title">
                          <span className="notification-mute-icons">
                            <VolumeOff className="notification-mute-icon" />
                          </span>
                          {t('notificationSettings.mute.label')}
                        </Typography>
                        <FormControl className={`notification-select notification-mute-select ${preferences.muted_notification_for_next ? 'selected' : ''}`}>
                          <Select
                            value={muteSelection}
                            onChange={handleMuteDurationChange}
                            displayEmpty
                            className="notification-select-input"
                          >
                            {NOTIFICATION_SETTINGS.MUTE_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value} className="notification-menu-item">
                                {t(option.labelKey)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </>
        ) : (
          <TeamNotifications
            selectedProjectId={teamSelectedProject?.id || null}
            onProjectChange={(projectId) => {
              setTeamSelectedProjectId(projectId);
              persistProjectSelectionChange(mySelectedProjectId, projectId);
            }}
          />
        )}
      </main>
    </div>
  );
};

export default NotificationSettings;
